/**
 * Utilidades para validar blisters y sus precios
 */

/**
 * Verifica si un blister es válido para agregar al carrito
 * @param {Object} blister - El objeto blister a validar
 * @returns {Object} - { isValid: boolean, reason: string }
 */
export const validateBlisterForCart = (blister) => {
  if (!blister) {
    return { isValid: false, reason: 'No se ha seleccionado una presentación' };
  }

  if (!blister.price || blister.price <= 0) {
    return { isValid: false, reason: 'Este producto no tiene precio asignado' };
  }

  if (blister.stock <= 0) {
    return { isValid: false, reason: 'No hay stock disponible' };
  }

  return { isValid: true, reason: '' };
};

/**
 * Filtra blisters que son válidos para mostrar
 * @param {Array} blisters - Array de blisters
 * @returns {Array} - Array de blisters válidos
 */
export const getValidBlisters = (blisters) => {
  if (!Array.isArray(blisters)) return [];
  
  return blisters.filter(blister => 
    blister.price && blister.price > 0
  );
};

/**
 * Obtiene el primer blister disponible (priorizando con stock, pero permitiendo sin stock)
 * @param {Array} blisters - Array de blisters
 * @returns {Object|null} - El primer blister disponible o null
 */
export const getFirstAvailableBlister = (blisters) => {
  if (!Array.isArray(blisters)) return null;
  
  // Buscar primero uno con stock y precio
  const availableWithStock = blisters.find(b => 
    b.stock > 0 && b.price && b.price > 0
  );
  
  if (availableWithStock) return availableWithStock;
  
  // Si no hay con stock, buscar al menos uno con precio (aunque no tenga stock)
  const availableWithPrice = blisters.find(b => 
    b.price && b.price > 0
  );
  
  if (availableWithPrice) return availableWithPrice;
  
  // Como último recurso, tomar el primero disponible (incluso sin precio ni stock)
  return blisters.length > 0 ? blisters[0] : null;
};

/**
 * Verifica si un blister debe estar deshabilitado en el dropdown
 * @param {Object} blister - El objeto blister
 * @returns {boolean} - true si debe estar deshabilitado
 */
export const isBlisterDisabled = (blister) => {
  // Ya no deshabilitar blisters sin stock, solo sin precio
  return !blister || (!blister.price || blister.price <= 0);
};

/**
 * Obtiene el texto de estado de un blister
 * @param {Object} blister - El objeto blister
 * @returns {string} - Texto descriptivo del estado
 */
export const getBlisterStatusText = (blister) => {
  if (!blister) return '';
  
  if (!blister.price || blister.price <= 0) {
    return '';
  }
  
  if (blister.stock <= 0) {
    return '';
  }
  
  return '';
};

/**
 * Verifica si se debe mostrar el precio de un blister
 * @param {Object} blister - El objeto blister
 * @returns {boolean} - true si se debe mostrar el precio
 */
export const shouldShowPrice = (blister) => {
  return blister && blister.price && blister.price > 0;
};

/**
 * Calcula el precio con descuento
 * @param {Object} blister - El objeto blister
 * @returns {number} - Precio final con descuento aplicado
 */
export const calculateDiscountedPrice = (blister) => {
  if (!blister || !blister.price || blister.price <= 0) return 0;
  
  if (!blister.discount || blister.discount <= 0) {
    return parseFloat(blister.price);
  }
  
  return parseFloat(blister.price * (1 - blister.discount / 100));
};
